/**
 * Chatbot API client.
 *
 * Public endpoint `/api/chatbot/ask` returns one of two response shapes:
 *   - { type: 'clarify', text, options, sessionToken, tokens }
 *   - { type: 'answer', text, sources, sessionToken, tokens }
 *
 * Admin endpoints (`/ingest`, `/docs`, `/test`) require Admin or
 * SupperAdmin — used by the training page in /dashboard/chatbot-training.
 */

import axios from 'axios';

export type ApiRes<T> = { EM: string; EC: number; DT: T };

export type ChatHistoryMessage = {
  role: 'user' | 'bot';
  text: string;
};

export type ChatbotClarifyResp = {
  type: 'clarify';
  text: string;
  options: string[];
  sessionToken: string;
  tokens: number;
};

export type FeeBreakdownItem = {
  partCode: string;
  partName: string;
  amount: number;
  diem: number | null;
};

export type FeeData = {
  cccd: string;
  hoTen: string;
  vehicleType: string | null;
  kySatHach: string;
  ngaySH: string;
  breakdown: FeeBreakdownItem[];
  totalAmount: number;
  bank: { bankCode: string; bankName: string; accountNumber: string; accountHolder: string } | null;
  qrUrl: string | null;
};

export type ChatbotAnswerResp = {
  type: 'answer';
  text: string;
  sources: string[];
  sessionToken: string;
  tokens: number;
  feeData?: FeeData | null;
};

export type ChatbotAskResp = ChatbotClarifyResp | ChatbotAnswerResp;

export type ChatbotDoc = {
  id: number;
  adminId: number;
  extId: string;
  question: string;
  answer: string;
  tags: string[] | null;
  unit: string | null;
  sourceLabel: string | null;
  active: boolean;
  createdAt: string;
  updatedAt: string;
};

export type ChatbotIngestResult = {
  inserted: number;
  updated: number;
  skipped: number;
  errors: { line: number; message: string }[];
};

export type ChatbotTestResult = {
  type: 'clarify' | 'answer';
  text: string;
  options?: string[];
  sources?: string[];
  tokens: number;
  retrievedChunks: Array<{
    sim: number;
    docId: number;
    extId: string;
    question: string;
    unit: string | null;
    sourceLabel: string | null;
    chunkContent: string;
  }>;
  candidateUnits: string[];
};

/** Anonymous-friendly call. FE attaches the auth token if present (the
 *  axios interceptor handles that), but BE allows unauthenticated. */
export const chatbotAskApi = (payload: {
  question: string;
  sessionToken?: string | null;
  history?: ChatHistoryMessage[];
  adminId?: number;
}) =>
  axios.post<ApiRes<ChatbotAskResp>>('/api/chatbot/ask', payload).then((r) => r.data);

export type ChatbotHealth = {
  hasData: boolean;
  docCount: number;
  unitsCount: number;
  configured: boolean;
};

/** Public empty-state probe — tells the FE whether the bot is configured
 *  and has training data, so the welcome message can be honest. */
export const chatbotHealthApi = () =>
  axios.get<ApiRes<ChatbotHealth>>('/api/chatbot/health').then((r) => r.data);

export const chatbotIngestApi = (file: File, forceUnit?: string) => {
  const fd = new FormData();
  fd.append('file', file);
  return axios.post<ApiRes<ChatbotIngestResult>>('/api/chatbot/ingest', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
    params: forceUnit ? { forceUnit } : undefined,
    timeout: 600_000, // ingestion may take a while if many entries
  }).then((r) => r.data);
};

export type ChatbotUnitSummary = {
  unit: string;
  docCount: number;
  chunkCount: number;
  lastUpdate: string | null;
};

export const chatbotListUnitsApi = () =>
  axios.get<ApiRes<ChatbotUnitSummary[]>>('/api/chatbot/units').then((r) => r.data);

export const chatbotDeleteUnitApi = (unit: string) =>
  axios.delete<ApiRes<{ deletedDocs: number; deletedChunks: number }>>(
    `/api/chatbot/units/${encodeURIComponent(unit)}`,
  ).then((r) => r.data);

export type ChatbotCenter = {
  id: number;
  code: string;       // 'admin-94'
  name: string;       // display name (admin's username)
  email: string;
};

export const chatbotListCentersApi = () =>
  axios.get<ApiRes<ChatbotCenter[]>>('/api/chatbot/centers').then((r) => r.data);

export type ChatbotDocsList = {
  items: ChatbotDoc[];
  total: number;
  limit: number;
  offset: number;
};

export const chatbotListDocsApi = (
  params?: { search?: string; unit?: string; limit?: number; offset?: number; countOnly?: boolean }
) =>
  axios.get<ApiRes<ChatbotDocsList>>('/api/chatbot/docs', {
    params: params
      ? { ...params, countOnly: params.countOnly ? 1 : undefined }
      : undefined,
  }).then((r) => r.data);

export const chatbotImportKqshXmlApi = (file: File, vehicleType: 'moto' | 'oto') => {
  const fd = new FormData();
  fd.append('file', file);
  fd.append('vehicleType', vehicleType);
  return axios.post<ApiRes<{ inserted: number; updated: number; skipped: number; errors: { row: number; message: string; cccd?: string }[] }>>('/api/admin/kqsh/import-xml', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 600_000,
  }).then((r) => r.data);
};

export const chatbotDeleteDocApi = (id: number) =>
  axios.delete<ApiRes<null>>(`/api/chatbot/docs/${id}`).then((r) => r.data);

export const chatbotTestApi = (question: string) =>
  axios.post<ApiRes<ChatbotTestResult>>('/api/chatbot/test', { question })
    .then((r) => r.data);

// ── Retake fee management ─────────────────────────────────────────────────

export type RetakeFee = {
  id: number;
  adminId: number;
  vehicleType: string;
  partCode: string;
  partName: string;
  amount: number;
  createdAt: string;
  updatedAt: string;
};

export const listRetakeFeesApi = () =>
  axios.get<ApiRes<RetakeFee[]>>('/api/admin/retake-fees').then((r) => r.data);

export const upsertRetakeFeeApi = (payload: { vehicleType: string; partCode: string; partName: string; amount: number }) =>
  axios.post<ApiRes<RetakeFee>>('/api/admin/retake-fees', payload).then((r) => r.data);

export const deleteRetakeFeeApi = (id: number) =>
  axios.delete<ApiRes<null>>(`/api/admin/retake-fees/${id}`).then((r) => r.data);

// ── Bank account management ───────────────────────────────────────────────

export type BankAccount = {
  id: number;
  adminId: number;
  bankCode: string;
  bankName: string;
  accountNumber: string;
  accountHolder: string;
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
};

export const listBankAccountsApi = () =>
  axios.get<ApiRes<BankAccount[]>>('/api/admin/bank-accounts').then((r) => r.data);

export const createBankAccountApi = (payload: Omit<BankAccount, 'id' | 'adminId' | 'createdAt' | 'updatedAt'>) =>
  axios.post<ApiRes<BankAccount>>('/api/admin/bank-accounts', payload).then((r) => r.data);

export const updateBankAccountApi = (id: number, payload: Partial<Omit<BankAccount, 'id' | 'adminId' | 'createdAt' | 'updatedAt'>>) =>
  axios.put<ApiRes<BankAccount>>(`/api/admin/bank-accounts/${id}`, payload).then((r) => r.data);

export const deleteBankAccountApi = (id: number) =>
  axios.delete<ApiRes<null>>(`/api/admin/bank-accounts/${id}`).then((r) => r.data);
